import React, { useCallback } from 'react';
import type { FC } from 'react';
import styled from 'styled-components';
import { DynamicImage, QuizAnswerButton } from 'components';
import { mobile, tablet } from 'styles/breakpoints';
import Header from './Header';
import ContinueButton from './QuizContinueButton';
import { Question, QuestionOption } from 'types/quiz';

interface MultipleImageProps {
  question: Question;
  selectedOptions: QuestionOption[];
  disabled: boolean;
  onSelect: (option: QuestionOption) => void;
  onContinue: () => void;
}

const Container = styled.div`
  @media ${tablet} {
    padding-bottom: 10rem;
  }
`;

const OptionsContainer = styled.div`
  margin: 0 auto;
  max-width: 71.25rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  @media ${tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Option = styled(QuizAnswerButton)`
  height: 7.75rem;
`;

const StyledDynamicImage = styled(DynamicImage)<{
  imageHeightDesktop: string;
  imageHeightTablet: string;
  imageHeightMobile: string;
}>`
  height: ${({ imageHeightDesktop }) => imageHeightDesktop};

  @media ${tablet} {
    height: ${({ imageHeightTablet }) => imageHeightTablet};
  }

  @media ${mobile} {
    height: ${({ imageHeightMobile }) => imageHeightMobile};
  }
`;

const MultipleImage: FC<MultipleImageProps> = ({
  question,
  selectedOptions,
  disabled,
  onSelect,
  onContinue,
}) => {
  const { type, label, image, alt, optionsLabel, options } = question;

  const selected = useCallback(
    (option: QuestionOption) =>
      selectedOptions.some(
        selectedOption => selectedOption.value === option.value,
      ),
    [selectedOptions],
  );

  const renderOption = (option: QuestionOption, index: number) => {
    const key = `${type}-answer-${index}`;
    return option.image ? (
      <Option
        key={key}
        onClick={() => onSelect(option)}
        active={selected(option)}
        ImageComponent={<DynamicImage src={option.image} alt={option.value} />}
      >
        {option.label}
      </Option>
    ) : null;
  };

  return (
    <Container>
      <Header
        title={label}
        subtitle={optionsLabel}
        imageUrl={image}
        imageAlt={alt}
        imageHeightDesktop={question.imageHeightDesktop}
        imageHeightTablet={question.imageHeightTablet}
        imageHeightMobile={question.imageHeightMobile}
      />
      <OptionsContainer>{options?.map(renderOption)}</OptionsContainer>
      <ContinueButton
        title={question?.nextBtnTitle}
        onClick={onContinue}
        disabled={disabled}
      />
    </Container>
  );
};

export default MultipleImage;
